/// <reference types="vite/client" />

/**
 * Centralized environment configuration
 *
 * Environment variables are loaded from:
 * - Local development: .envrc (via direnv)
 * - Production/Netlify: Environment variables in deployment platform
 *
 * All environment variables must be prefixed with VITE_ to be available in the client bundle.
 */
export const env = {
  // Build environment
  NODE_ENV: import.meta.env.MODE,

  // Application environment
  INFRAGENTS_ENV: import.meta.env.VITE_INFRAGENTS_ENV || "dev",

  // API Configuration
  API_BASE_URL: import.meta.env.VITE_API_BASE_URL,
  API_KEY: import.meta.env.VITE_API_KEY,
  HEALTH_URL: import.meta.env.VITE_HEALTH_URL,

  // Application Configuration
  MAINTENANCE_MODE: import.meta.env.VITE_MAINTENANCE_MODE === "true",
  APP_NAME: import.meta.env.VITE_APP_NAME || "InfrAgents",
  APP_VERSION: import.meta.env.VITE_APP_VERSION || "1.0.0",

  // Analytics Configuration
  GA_MEASUREMENT_ID: import.meta.env.VITE_GA_MEASUREMENT_ID,

  // Computed environment flags
  IS_PRODUCTION: import.meta.env.VITE_INFRAGENTS_ENV === "prd",
  IS_DEVELOPMENT: import.meta.env.VITE_INFRAGENTS_ENV === "dev",
  IS_STAGING: import.meta.env.VITE_INFRAGENTS_ENV === "staging",
} as const;

export type Environment = typeof env;

// Validate required environment variables
const requiredEnvVars = ["VITE_INFRAGENTS_ENV"] as const;

for (const envVar of requiredEnvVars) {
  if (!import.meta.env[envVar]) {
    if (env.IS_DEVELOPMENT) {
      // eslint-disable-next-line no-console
      console.warn(
        `Missing environment variable: ${envVar}. Using default value.`,
      );
    }
  }
}

// Environment-specific configuration
export const apiConfig = {
  BASE_URL: env.API_BASE_URL,
  API_KEY: env.API_KEY,
  HEALTH_URL: env.HEALTH_URL,
  timeout: env.IS_PRODUCTION ? 10000 : 5000,
  retries: env.IS_PRODUCTION ? 3 : 1,
} as const;

// Debug configuration
export const debugConfig = {
  enableLogging: env.IS_DEVELOPMENT,
  enableApiLogging: env.IS_DEVELOPMENT,
  enablePerformanceMonitoring: env.IS_PRODUCTION,
} as const;

// Analytics configuration
export const analyticsConfig = {
  measurementId: env.GA_MEASUREMENT_ID,
  enabled: env.IS_PRODUCTION || env.IS_DEVELOPMENT, // Enable in prod and dev, disable in staging
  anonymizeIp: true,
  allowAdPersonalization: env.IS_PRODUCTION,
  debugMode: env.IS_DEVELOPMENT,
} as const;
